import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './CTASection.module.css';
import useScrollAnimation from '../../hooks/useScrollAnimation';
import { countryCodes } from './countryCodes';
import { useLocation } from 'react-router-dom';

const CTASection = () => {
    const { t } = useTranslation();
    const sectionRef = useScrollAnimation({ once: true });
    const location = useLocation();
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phoneCountryCode: '+1',
        phone: '',
        interest: 'general_inquiry',
        message: ''
    });
    const [popupInfo, setPopupInfo] = useState({ visible: false, message: '', type: '' });

    useEffect(() => {
        const hash = location.hash;
        let interestParam = null;
        if (hash) {
            const queryIndex = hash.indexOf('?');
            if (queryIndex !== -1) {
                const search = hash.substring(queryIndex);
                const params = new URLSearchParams(search);
                interestParam = params.get('interest');
            }
        }
        const validInterests = ['cereal_sensors', 'potato_sensors', 'software_platform', 'general_inquiry'];
        if (interestParam && validInterests.includes(interestParam)) {
            setFormData(prevData => {
                if (prevData.interest !== interestParam) {
                    return { ...prevData, interest: interestParam };
                }
                return prevData;
            });
        } else {
            setFormData(prevData => {
                if (prevData.interest !== 'general_inquiry') {
                    return { ...prevData, interest: 'general_inquiry' };
                }
                return prevData;
            });
        }
    }, [location]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevState => ({
            ...prevState,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.name || !formData.email) {
            setPopupInfo({ visible: true, message: t('cta.alert.requiredFields'), type: 'error' });
            return;
        }

        try {
            const response = await fetch('https://cta-form-handler.evs-abyss.workers.dev/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData),
            });

            if (!response.ok) {
                const userFriendlyError = `Submission failed. Please try again. (Status: ${response.status})`;
                throw new Error(userFriendlyError);
            }

            if (window.dataLayer) {
                window.dataLayer.push({
                    event: 'cta_form_submit',
                    form_interest: formData.interest,
                    form_email: formData.email
                });
            }

            setPopupInfo({ visible: true, message: t('cta.alert.success'), type: 'success' });
            setFormData({
                name: '',
                email: '',
                phoneCountryCode: '+1',
                phone: '',
                interest: 'general_inquiry',
                message: ''
            });
        } catch (err) {
            setPopupInfo({ visible: true, message: err.message || 'An unexpected error occurred.', type: 'error' });
        }
    };

    const closePopup = () => {
        setPopupInfo({ visible: false, message: '', type: '' });
    };

    return (
        <section id="cta-form" className={`${styles.ctaFormSection} animate-on-scroll`} ref={sectionRef}>
            {popupInfo.visible && (
                <div className={`${styles.popupOverlay} ${popupInfo.type === 'error' ? styles.popupError : styles.popupSuccess}`}>
                    <div className={styles.popupContent}>
                        <p>{popupInfo.message}</p>
                        <button onClick={closePopup} className={styles.popupCloseBtn}>
                            {t('cta.popup.closeButton') || 'Close'}
                        </button>
                    </div>
                </div>
            )}

            <div className="container">
                <h2 className={`section-title ${styles.sectionTitle}`}>{t('cta.title')}</h2>
                <p className={styles.ctaSubtitle}>{t('cta.subtitle')}</p>
                <form onSubmit={handleSubmit} className={styles.contactForm}>
                    <div className={styles.formGroup}>
                        <label htmlFor="name">{t('cta.form.nameLabel')}</label>
                        <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} required />
                    </div>
                    <div className={styles.formGroup}>
                        <label htmlFor="email">{t('cta.form.emailLabel')}</label>
                        <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required />
                    </div>
                    <div className={styles.formGroup}>
                        <label htmlFor="phone">{t('cta.form.phoneLabel')}</label>
                        <div style={{ display: 'flex', gap: '8px' }}>
                            <select
                                id="phoneCountryCode"
                                name="phoneCountryCode"
                                value={formData.phoneCountryCode}
                                onChange={handleChange}
                                className={styles.input}
                                style={{ width: '140px' }}
                            >
                                {countryCodes.map(({ code, name }) => (
                                    <option key={code} value={code}>{`${code} (${name})`}</option>
                                ))}
                            </select>
                            <input
                                type="tel"
                                id="phone"
                                name="phone"
                                value={formData.phone}
                                onChange={handleChange}
                                className={styles.input}
                                style={{ flex: 1 }}
                                placeholder="Phone number"
                            />
                        </div>
                    </div>
                    <div className={styles.formGroup}>
                        <label htmlFor="interest">{t('cta.form.interestLabel')}</label>
                        <select id="interest" name="interest" value={formData.interest} onChange={handleChange}>
                            <option value="cereal_sensors">{t('cta.form.interestOptions.cereal')}</option>
                            <option value="potato_sensors">{t('cta.form.interestOptions.potato')}</option>
                            <option value="software_platform">{t('cta.form.interestOptions.software')}</option>
                            <option value="general_inquiry">{t('cta.form.interestOptions.general')}</option>
                        </select>
                    </div>
                    <div className={styles.formGroup}>
                        <label htmlFor="message">{t('cta.form.messageLabel')}</label>
                        <textarea id="message" name="message" value={formData.message} onChange={handleChange} rows="2"></textarea>
                    </div>
                    <button type="submit" className={`btn btn-primary ${styles.submitBtn}`}>{t('cta.form.leaveInquiryButton')}</button>
                </form>
            </div>
        </section>
    );
};

export default CTASection;
